import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const workflowType = z.union([
  z.literal("pricing"),
  z.literal("sp"),
  z.literal("ads"),
  z.literal("dsp"),
  z.literal("wm"),
  z.literal("ntm"),
  z.literal("both"),
  z.literal("wm-mp"),
  z.literal("shopify"),
]);
export type WorkflowType = z.infer<typeof workflowType>;

const report_status = z.union([
  z.literal("P"),
  z.literal("S"),
  z.literal("F"),
  z.literal("K"),
  z.literal("E"),
  z.literal("N/A"),
]);
export type ReportStatus = z.infer<typeof report_status>;

const WORKFLOW_PROGRESS_STATUS_BY_CODE = {
  0: "PENDING",
  1: "RUNNING",
  3: "SUCCESS",
  4: "FAILED",
} as const;

const workflowProgressStatus = z
  .union([z.literal(0), z.literal(1), z.literal(3), z.literal(4)])
  .transform((code) => WORKFLOW_PROGRESS_STATUS_BY_CODE[code]);

const workflowPhaseGroup = z.union([z.literal("recent"), z.literal("backfill")]);

// [completed, total] count pair. Drive bars off the ratio, not a memorized
// total — totals can grow as work is discovered.
const progressPair = z.tuple([z.number(), z.number()]);

const workflowProgressSchema = z.object({
  status: workflowProgressStatus,
  phase_label: z.string().nullable(),
  description: z.string().nullable(),
  percent: z.number().nullable(),
  progress: progressPair,
  // Completed vs total pending reports (Fawcett). Drives the bar during
  // fetching_recent / backfilling_history; also the ARA workflow's progress.
  report_progress: progressPair.nullish(),
  // Rollup sub-tasks, non-null only on the recent workflow during
  // building_dashboards. Already folded into `percent`, so informational.
  rollup_progress: progressPair.nullish(),
  // SQPR requests landed, seller orgs only; also merged onto the recent
  // workflow's payload. `completed` can stay below `total` forever, so it's
  // informational and never gates completion.
  sqpr_progress: progressPair.nullish(),
  // When the SQPR backfill was dispatched — bounds "still importing" client-side.
  sqpr_dispatched_at: z.string().nullish(),
  backfill_workflow_id: z.string().nullable(),
  // Detached seller-analytics (ARA) workflow id, set on the recent workflow once
  // started. Seller orgs only. Poll it for ARA report progress.
  seller_analytics_workflow_id: z.string().nullish(),
  phase: z.string().nullish(),
  phase_group: workflowPhaseGroup.nullish(),
  phase_index: z.number().nullish(),
  phase_count: z.number().nullish(),
  is_usable: z.boolean().nullish(),
});

const workflowSchema = z.object({
  id: z.string(),
  tracking_id: z.string(),
  tracking_context: z.object({
    org_id: z.number(),
  }),
  status: z.union([
    z.literal("PENDING"),
    z.literal("RUNNING"),
    z.literal("SUCCESS"),
    z.literal("FAILED"),
    z.literal("CANCELLED"),
  ]),
  is_finished: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),

  task_context: z.object({
    tasks: z.record(
      z.string(),
      z.object({
        job_id: z.string(),
        start_time: z.string(),
      }),
    ),
    download_listings_tasks: z.record(z.string(), z.string().datetime().nullable()),
    pending_reports: z.record(
      z.string(),
      z.array(
        z.object({
          id: z.number(),
          plan: z.number(),
          entryId: z.number().optional(),
          credential: z.unknown(),
          pending_report: z
            .object({
              id: z.number(),
              report_type: z.string(),
              status: report_status,
              credential: z.number(),
              wait_until: z.string().datetime().nullable(),
              payload: z.object({
                marketplace_ids: z.string().array().optional(),
                execution_options: z
                  .object({
                    reportDate: z.string().optional().nullable(),
                    reportType: z.string().optional(),
                    startDate: z.string().optional().nullable(),
                    endDate: z.string().optional().nullable(),
                    dataEndTime: z.string().date().optional().nullable(),
                    dataStartTime: z.string().date().optional().nullable(),
                    reportOptions: z.unknown().nullable(),
                    marketplaceIds: z.string().array().optional(),
                  })
                  .optional(),
                report_id: z.string().optional().nullable(),
              }),
              warehouse: z
                .object({
                  id: z.number(),
                })
                .nullable(),
            })
            .passthrough(),
        }),
      ),
    ),
  }),
});
export type Workflow = z.infer<typeof workflowSchema>;
export type WorkflowProgress = z.infer<typeof workflowProgressSchema>;

export const workflowsApiDefinitions = prefixApi(
  "",
  apiBuilder({
    alias: "startWorkflow",
    method: "post",
    path: "/workflows/start/",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ wft: workflowType }),
      },
    ],
    status: 200,
    response: z.object({ status: z.literal("success") }),
  })
    .addEndpoint({
      alias: "getWorkflows",
      method: "get",
      path: "/api/workflow/",
      parameters: [{ name: "page_size", type: "Query", schema: z.number() }],
      status: 200,
      response: z.object({
        count: z.number(),
        next: z.unknown(),
        previous: z.unknown(),
        results: workflowSchema.array(),
      }),
    })
    .addEndpoint({
      alias: "getWorkflowState",
      method: "get",
      path: "/api/workflows/:trackingId/",
      status: 200,
      response: z.object({
        workflow: z.object({
          callbacks: z.unknown(),
          directed: z.boolean(),
          graph: z.unknown(),
          links: z.unknown().array(),
          multigraph: z.boolean(),
          nodes: z.unknown().array(),
        }),
        state: z.object({
          status: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
        }),
        job_states: z.record(
          z.string(),
          z.object({
            status: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
            retries: z.number(),
            context: z.unknown(),
            exception: z.unknown().nullable(),
          }),
        ),
        outputs: z.object({}),
      }),
    })
    .addEndpoint({
      alias: "getWorkflowProgress",
      method: "get",
      path: "/api/workflows/:trackingId/progress",
      status: 200,
      response: workflowProgressSchema,
    })
    .build(),
);
